import React, { useEffect, useState } from 'react';
import { fetchHelpRequestPrefill, submitHelpRequest } from '../../api/helpRequest';
import supportHeadset from '../../assets/images/support-headset.png';
import { AuthHeader, defaultAuthMenuItems } from '../auth/AuthHeader';
import '../auth/LoginPage.css';
import './HelpRequestPage.css';

declare global {
  interface Window {
    grecaptcha: any;
  }
}

type FormValues = {
  username: string;
  orgName: string;
  emailAddress: string;
  phone: string;
  message: string;
  honeypot: string;
};

type FieldErrors = Partial<Record<keyof FormValues, string>>;

const usernameRegex = /^[a-zA-Z ]+$/;
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const RECAPTCHA_ACTION = 'help_request';

const loadRecaptchaV3 = (siteKey: string) => {
  if (typeof window === 'undefined') return Promise.reject();
  if (window.grecaptcha && window.grecaptcha.ready) {
    return Promise.resolve();
  }

  const existingScript = document.getElementById('recaptcha-script-v3') as HTMLScriptElement | null;
  if (existingScript) {
    return new Promise<void>((resolve, reject) => {
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener('error', () => reject(), { once: true });
    });
  }

  return new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.id = 'recaptcha-script-v3';
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject();
    document.body.appendChild(script);
  });
};

const executeRecaptchaV3 = async (siteKey: string): Promise<string> => {
  if (!window.grecaptcha || !window.grecaptcha.ready) {
    throw new Error('recaptcha-not-ready');
  }

  return new Promise((resolve, reject) => {
    window.grecaptcha.ready(() => {
      window.grecaptcha
        .execute(siteKey, { action: RECAPTCHA_ACTION })
        .then((token: string) => {
          if (token) {
            resolve(token);
          } else {
            reject();
          }
        })
        .catch(() => reject());
    });
  });
};

export const HelpRequestPage: React.FC = () => {
  const [values, setValues] = useState<FormValues>({
    username: '',
    orgName: '',
    emailAddress: '',
    phone: '',
    message: '',
    honeypot: '',
  });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [statusMessage, setStatusMessage] = useState('');
  const [statusTone, setStatusTone] = useState<'success' | 'error' | ''>('');
  const [prefillLoading, setPrefillLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [captchaError, setCaptchaError] = useState('');
  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

  useEffect(() => {
    let isMounted = true;
    setPrefillLoading(true);

    fetchHelpRequestPrefill()
      .then((data) => {
        if (!isMounted) {
          return;
        }

        setValues((current) => ({
          ...current,
          username: current.username || data.username || (data as any)?.name || '',
          orgName: current.orgName || data.orgName || (data as any)?.organization || '',
          emailAddress:
            current.emailAddress || data.emailAddress || (data as any)?.email || (data as any)?.email_address || '',
          phone: current.phone || data.phone || (data as any)?.phoneNumber || '',
        }));
      })
      .catch(() => {
        // Prefill is optional; ignore errors.
      })
      .finally(() => {
        if (isMounted) {
          setPrefillLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const formDisabled = isSubmitted;

  const handleChange = (field: keyof FormValues) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setValues((current) => ({ ...current, [field]: event.target.value }));
  };

  const validate = (): boolean => {
    const nextErrors: FieldErrors = {};
    const trimmedUsername = values.username.trim();
    const trimmedEmail = values.emailAddress.trim();
    const trimmedOrgName = values.orgName.trim();
    const trimmedPhone = values.phone.trim();
    const trimmedMessage = values.message.trim();
    const honeypotValue = values.honeypot.trim();

    if (honeypotValue) {
      nextErrors.honeypot = 'You appear to be a non-human bot.';
    }

    if (!trimmedUsername) {
      nextErrors.username = 'Username is required.';
    } else if (!usernameRegex.test(trimmedUsername)) {
      nextErrors.username = 'Only letters and spaces are allowed.';
    }

    if (!trimmedOrgName) {
      nextErrors.orgName = 'Organization name is required.';
    }

    if (!trimmedEmail) {
      nextErrors.emailAddress = 'Email address is required.';
    } else if (!emailRegex.test(trimmedEmail)) {
      nextErrors.emailAddress = 'Enter a valid email address.';
    }

    if (!trimmedPhone) {
      nextErrors.phone = 'Phone is required.';
    }

    if (!trimmedMessage) {
      nextErrors.message = 'Message is required.';
    }

    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      setStatusTone('error');
      setStatusMessage(nextErrors.honeypot || 'Look for error messages below.');
      return false;
    }

    setErrors({});
    setStatusMessage('');
    setStatusTone('');
    return true;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isSubmitting || formDisabled) {
      return;
    }

    const isValid = validate();
    if (!isValid) {
      return;
    }

    setIsSubmitting(true);
    setStatusMessage('');
    setStatusTone('');
    setCaptchaError('');

    let tokenToSend = '';
    try {
      if (!recaptchaSiteKey) {
        throw new Error('missing-site-key');
      }
      await loadRecaptchaV3(recaptchaSiteKey);
      tokenToSend = await executeRecaptchaV3(recaptchaSiteKey);
    } catch {
      setStatusTone('error');
      setStatusMessage('Captcha verification failed. Please try again.');
      setCaptchaError('Captcha verification failed. Please try again.');
      setIsSubmitting(false);
      return;
    }

    try {
      await submitHelpRequest({
        username: values.username.trim(),
        orgName: values.orgName.trim(),
        emailAddress: values.emailAddress.trim(),
        phone: values.phone.trim(),
        message: values.message.trim(),
        honeypot: values.honeypot.trim(),
        captchaToken: tokenToSend,
        recaptchaToken: tokenToSend,
        gRecaptchaResponse: tokenToSend,
      });

      setIsSubmitted(true);
      setStatusTone('success');
      setStatusMessage('Your Message Has Been Sent.');
    } catch (error: any) {
      const status = error?.response?.status;
      const data = error?.response?.data || {};

      if (status === 403) {
        setStatusTone('error');
        setStatusMessage('Submissions from your country are not allowed.');
        return;
      }

      if (status === 400 || status === 422) {
        const backendErrors: FieldErrors = {};
        const incomingErrors = data.errors || data.error;
        const messageText = data.message || data.error;

        if (incomingErrors && typeof incomingErrors === 'object') {
          (['username', 'orgName', 'emailAddress', 'phone', 'message', 'honeypot'] as Array<keyof FormValues>).forEach(
            (field) => {
              const fieldError = incomingErrors[field];
              if (Array.isArray(fieldError)) {
                backendErrors[field] = fieldError.join(' ');
              } else if (typeof fieldError === 'string') {
                backendErrors[field] = fieldError;
              }
            },
          );
        }

        const isCaptchaFailure =
          (typeof incomingErrors === 'object' &&
            ((incomingErrors as any)?.captcha ||
              (incomingErrors as any)?.recaptcha ||
              (incomingErrors as any)?.captchaToken ||
              (incomingErrors as any)?.recaptchaToken ||
              (incomingErrors as any)?.gRecaptchaResponse)) ||
          (typeof messageText === 'string' && messageText.toLowerCase().includes('captcha'));

        if (isCaptchaFailure) {
          setStatusTone('error');
          setStatusMessage('Captcha verification failed. Please try again.');
          setCaptchaError('Captcha verification failed. Please try again.');
          setErrors(backendErrors);
          return;
        }

        if (backendErrors.honeypot) {
          setStatusTone('error');
          setStatusMessage('You appear to be a non-human bot.');
        } else if (data.message) {
          setStatusTone('error');
          setStatusMessage(data.message);
        } else {
          setStatusTone('error');
          setStatusMessage('Look for error messages below.');
        }

        setErrors(backendErrors);
        return;
      }

      setStatusTone('error');
      setStatusMessage('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="help-request-page">
      <AuthHeader menuItems={defaultAuthMenuItems} />
      <main className="help-request-main">
        <div className="help-request-card">

        <div className="help-request-heading">
          <h1 className="help-request-title">Help Request</h1>
          <span className="help-request-underline" aria-hidden="true" />
        </div>

        <div className="help-request-intro">
          <p className="help-request-intro-text">Complete the form below or email us.</p>
          <img
            className="help-request-avatar"
            src={supportHeadset}
            alt="Support agent"
          />
          <div className="help-request-email">
            <span className="help-request-mail-icon" aria-hidden="true">
              ✉
            </span>
            <a href="mailto:support@zynity.com" className="help-request-mail-link">
              support@zynity.com
            </a>
          </div>
        </div>

        <div className="help-request-description">
          Provide enough detail for us to quickly assist you.
          <br />
          We're standing by!
        </div>
        <div className="help-request-required-note" aria-live="polite">
          * all inputs required
        </div>

        {statusMessage && (
          <div
            className={`help-request-status ${
              statusTone === 'success' ? 'help-request-status--success' : 'help-request-status--error'
            }`}
            role={statusTone === 'error' ? 'alert' : 'status'}
            aria-live="polite"
          >
            {statusMessage}
          </div>
        )}
        {captchaError && (
          <div className="help-request-status help-request-status--error" role="alert">
            {captchaError}
          </div>
        )}

        <form className="help-request-form" onSubmit={handleSubmit} noValidate>
          <div className="help-request-grid">
            <div className="help-request-field">
            <label className="help-request-label" htmlFor="username">
              Username *
            </label>
            <div className="help-request-input-wrapper">
              <input
                id="username"
                name="username"
                type="text"
                autoComplete="name"
                placeholder="Your Name..."
                className="help-request-input"
                value={values.username}
                onChange={handleChange('username')}
                disabled={formDisabled}
                aria-invalid={Boolean(errors.username)}
                aria-describedby={errors.username ? 'username-error' : undefined}
              />
              {prefillLoading && <span className="help-request-inline-spinner" aria-hidden="true" />}
            </div>
            {errors.username && (
              <div id="username-error" className="help-request-error" role="alert">
                {errors.username}
              </div>
            )}
          </div>

            <div className="help-request-field">
            <label className="help-request-label" htmlFor="orgName">
              Organization *
            </label>
            <div className="help-request-input-wrapper">
              <input
                id="orgName"
                name="orgName"
                type="text"
                autoComplete="organization"
                placeholder="Organization Name..."
                className="help-request-input"
                value={values.orgName}
                onChange={handleChange('orgName')}
                disabled={formDisabled}
                aria-invalid={Boolean(errors.orgName)}
                aria-describedby={errors.orgName ? 'orgName-error' : undefined}
              />
              {prefillLoading && <span className="help-request-inline-spinner" aria-hidden="true" />}
            </div>
            {errors.orgName && (
              <div id="orgName-error" className="help-request-error" role="alert">
                {errors.orgName}
              </div>
            )}
          </div>

            <div className="help-request-field">
            <label className="help-request-label" htmlFor="emailAddress">
              Email Address *
            </label>
            <div className="help-request-input-wrapper">
              <input
                id="emailAddress"
                name="emailAddress"
                type="email"
                autoComplete="email"
                placeholder="Email..."
                className="help-request-input"
                value={values.emailAddress}
                onChange={handleChange('emailAddress')}
                disabled={formDisabled}
                aria-invalid={Boolean(errors.emailAddress)}
                aria-describedby={errors.emailAddress ? 'emailAddress-error' : undefined}
              />
              {prefillLoading && <span className="help-request-inline-spinner" aria-hidden="true" />}
            </div>
            {errors.emailAddress && (
              <div id="emailAddress-error" className="help-request-error" role="alert">
                {errors.emailAddress}
              </div>
            )}
          </div>

            <div className="help-request-field">
            <label className="help-request-label" htmlFor="phone">
              Phone *
            </label>
            <div className="help-request-input-wrapper">
              <input
                id="phone"
                name="phone"
                type="tel"
                autoComplete="tel"
                placeholder="Phone Number..."
                className="help-request-input"
                value={values.phone}
                onChange={handleChange('phone')}
                disabled={formDisabled}
                aria-invalid={Boolean(errors.phone)}
                aria-describedby={errors.phone ? 'phone-error' : undefined}
              />
              {prefillLoading && <span className="help-request-inline-spinner" aria-hidden="true" />}
            </div>
            {errors.phone && (
              <div id="phone-error" className="help-request-error" role="alert">
                {errors.phone}
              </div>
            )}
          </div>

            <div className="help-request-field help-request-field--full">
            <label className="help-request-label" htmlFor="message">
              Message *
            </label>
            <div className="help-request-input-wrapper">
              <textarea
                id="message"
                name="message"
                placeholder="Message..."
                className="help-request-textarea"
                value={values.message}
                onChange={handleChange('message')}
                disabled={formDisabled}
                aria-invalid={Boolean(errors.message)}
                aria-describedby={errors.message ? 'message-error' : undefined}
              />
              {prefillLoading && <span className="help-request-inline-spinner" aria-hidden="true" />}
            </div>
            {errors.message && (
              <div id="message-error" className="help-request-error" role="alert">
                {errors.message}
              </div>
            )}
          </div>
          </div>

          <div className="help-request-honeypot" aria-hidden="true">
            <label htmlFor="honeypot">Leave this field blank</label>
            <input
              id="honeypot"
              name="honeypot"
              type="text"
              autoComplete="off"
              value={values.honeypot}
              onChange={handleChange('honeypot')}
              tabIndex={-1}
            />
          </div>

          <button className="help-request-button" type="submit" disabled={isSubmitting || formDisabled}>
            {isSubmitting ? 'Sending…' : 'Submit'}
          </button>
        </form>
      </div>
      </main>
    </div>
  );
};
